import React, { useEffect, useRef, useState } from 'react';

const ServiceAreasMap = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [mapError, setMapError] = useState(false);

  // GA4 tracking function
  const trackMapEvent = (eventName, parameters = {}) => {
    try {
      if (window.gtag) {
        window.gtag('event', eventName, {
          event_category: 'map_interaction',
          event_label: 'service_areas_map',
          ...parameters
        });
      }
    } catch (error) {
      console.warn('GA4 tracking error:', error);
    }
  };

  useEffect(() => {
    const loadGoogleMaps = () => {
      try {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!apiKey) {
          console.warn('REACT_APP_GOOGLE_MAPS_API_KEY is not set');
          setMapError(true);
          return;
        }
        if (window.google && window.google.maps) {
          initializeMap();
          return;
        }
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=geometry`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          initializeMap();
          // Track map loaded event
          trackMapEvent('map_loaded', {
            map_type: 'service_areas',
            api_version: 'maps_js'
          });
        };
        script.onerror = (error) => {
          console.warn('Failed to load Google Maps API:', error);
          setMapError(true);
          // Track map load error
          trackMapEvent('map_load_error', {
            error_type: 'api_load_failed',
            map_type: 'service_areas'
          });
        };
        document.head.appendChild(script);
      } catch (error) {
        console.warn('Error loading Google Maps:', error);
        setMapError(true);
        trackMapEvent('map_load_error', {
          error_type: 'script_error',
          map_type: 'service_areas'
        });
      }
    };

    const initializeMap = () => {
      try {
        if (!mapRef.current || !window.google || !window.google.maps) {
          setMapError(true);
          return;
        }
        const center = { lat: -37.78, lng: 144.70 };
        const map = new window.google.maps.Map(mapRef.current, {
          zoom: 9,
          center: center,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'transit',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });
        mapInstanceRef.current = map;

        // Track map view event
        trackMapEvent('map_viewed', {
          map_type: 'service_areas',
          initial_zoom: map.getZoom(),
          center_lat: center.lat,
          center_lng: center.lng
        });

        // Track zoom changes
        map.addListener('zoom_changed', () => {
          trackMapEvent('map_zoom_changed', {
            new_zoom: map.getZoom(),
            map_type: 'service_areas'
          });
        });

        // Track map bounds changes
        map.addListener('bounds_changed', () => {
          const bounds = map.getBounds();
          if (bounds) {
            trackMapEvent('map_bounds_changed', {
              map_type: 'service_areas',
              bounds_sw_lat: bounds.getSouthWest().lat(),
              bounds_sw_lng: bounds.getSouthWest().lng(),
              bounds_ne_lat: bounds.getNorthEast().lat(),
              bounds_ne_lng: bounds.getNorthEast().lng()
            });
          }
        });

        // Improved service area coordinates - smoother boundary that properly encompasses all suburbs
        const serviceAreaCoords = [
          // Starting from the northwest (Bacchus Marsh area)
          { lat: -37.65, lng: 144.42 },   // Northwest of Bacchus Marsh
          { lat: -37.64, lng: 144.50 },   // North of Melton
          { lat: -37.65, lng: 144.58 },   // Adjusted to include all of Hillside
          { lat: -37.67, lng: 144.65 },   // North of Taylors Lakes/Keilor area
          { lat: -37.69, lng: 144.72 },   // North of St Albans
          { lat: -37.71, lng: 144.78 },   // North of Sunshine
          { lat: -37.73, lng: 144.85 },   // Northeast boundary
          
          // Eastern boundary
          { lat: -37.76, lng: 144.88 },   // East of Altona North
          { lat: -37.82, lng: 144.90 },   // East boundary near coast
          { lat: -37.88, lng: 144.85 },   // East of Point Cook
          { lat: -37.92, lng: 144.80 },   // Southeast boundary
          
          // Southern boundary
          { lat: -37.94, lng: 144.75 },   // South of Werribee
          { lat: -37.92, lng: 144.68 },   // South of Hoppers Crossing
          { lat: -37.90, lng: 144.60 },   // South of Wyndham Vale
          { lat: -37.88, lng: 144.52 },   // South of Manor Lakes
          
          // Western boundary
          { lat: -37.85, lng: 144.45 },   // West of Eynesbury
          { lat: -37.82, lng: 144.40 },   // Southwest boundary
          { lat: -37.78, lng: 144.35 },   // West of Caroline Springs
          { lat: -37.74, lng: 144.32 },   // West of Fraser Rise
          { lat: -37.70, lng: 144.35 },   // West of Taylors Hill
          { lat: -37.68, lng: 144.38 },   // West of Melton area
          { lat: -37.65, lng: 144.42 }    // Back to starting point
        ];

        const serviceArea = new window.google.maps.Polygon({
          paths: serviceAreaCoords,
          strokeColor: '#2E7D32',
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: '#4CAF50',
          fillOpacity: 0.2,
          map: map
        });

        // Track polygon interaction
        serviceArea.addListener('click', () => {
          trackMapEvent('service_area_polygon_clicked', {
            map_type: 'service_areas',
            polygon_type: 'coverage_area'
          });
        });

        // Service area markers with accurate coordinates
        const serviceAreas = [
          { name: 'Bacchus Marsh', lat: -37.6783, lng: 144.4394 },
          { name: 'Melton', lat: -37.6833, lng: 144.5728 },
          { name: 'Hillside', lat: -37.6956, lng: 144.7454 },
          { name: 'Taylors Lakes', lat: -37.7022, lng: 144.7768 },
          { name: 'Keilor', lat: -37.7239, lng: 144.8264 },
          { name: 'Fraser Rise', lat: -37.7500, lng: 144.6833 },
          { name: 'Taylors Hill', lat: -37.7167, lng: 144.7500 },
          { name: 'Delahey', lat: -37.7333, lng: 144.8000 },
          { name: 'St Albans', lat: -37.7456, lng: 144.8030 },
          { name: 'Sunshine', lat: -37.7667, lng: 144.8333 },
          { name: 'Altona North', lat: -37.8333, lng: 144.8333 },
          { name: 'Altona Meadows', lat: -37.8667, lng: 144.7833 },
          { name: 'Point Cook', lat: -37.9167, lng: 144.7500 },
          { name: 'Hoppers Crossing', lat: -37.8783, lng: 144.7017 },
          { name: 'Werribee', lat: -37.9000, lng: 144.6583 },
          { name: 'Wyndham Vale', lat: -37.8667, lng: 144.6167 },
          { name: 'Manor Lakes', lat: -37.8500, lng: 144.5833 },
          { name: 'Eynesbury', lat: -37.8167, lng: 144.5333 },
          { name: 'Caroline Springs', lat: -37.7431, lng: 144.7372 },
          { name: 'Thornhill Park', lat: -37.7333, lng: 144.7167 }
        ];

        serviceAreas.forEach((area) => {
          const marker = new window.google.maps.Marker({
            position: { lat: area.lat, lng: area.lng },
            map: map,
            title: area.name,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#2E7D32',
              fillOpacity: 0.8,
              strokeColor: '#FFFFFF',
              strokeWeight: 2
            }
          });

          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="padding: 8px; font-family: Arial, sans-serif;">
                <h3 style="margin: 0 0 8px 0; color: #2E7D32; font-size: 16px;">${area.name}</h3>
                <p style="margin: 0; color: #666; font-size: 14px;">Service Area</p>
                <p style="margin: 8px 0 0 0; color: #2E7D32; font-size: 12px;">
                  <a href="tel:0400000000" style="color: #2E7D32; text-decoration: none;">
                    📞 Call for Quote
                  </a>
                </p>
              </div>
            `
          });

          // Track marker click events
          marker.addListener('click', () => {
            infoWindow.open(map, marker);
            trackMapEvent('service_area_marker_clicked', {
              suburb_name: area.name,
              marker_lat: area.lat,
              marker_lng: area.lng,
              map_type: 'service_areas'
            });
          });

          // Track info window open events
          infoWindow.addListener('domready', () => {
            trackMapEvent('service_area_info_window_opened', {
              suburb_name: area.name,
              map_type: 'service_areas'
            });
          });
        });

        // Fit map to show all markers with some padding
        const bounds = new window.google.maps.LatLngBounds();
        serviceAreas.forEach(area => {
          bounds.extend({ lat: area.lat, lng: area.lng });
        });
        map.fitBounds(bounds);
        
        // Add a bit of padding to the bounds
        const extendedBounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(bounds.getSouthWest().lat() - 0.01, bounds.getSouthWest().lng() - 0.01),
          new window.google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01)
        );
        map.fitBounds(extendedBounds);

        // Track map fully loaded
        trackMapEvent('map_fully_loaded', {
          map_type: 'service_areas',
          markers_count: serviceAreas.length,
          final_zoom: map.getZoom()
        });

      } catch (error) {
        console.warn('Error initializing Google Maps:', error);
        setMapError(true);
        trackMapEvent('map_initialization_error', {
          error_type: 'initialization_failed',
          map_type: 'service_areas'
        });
      }
    };
    loadGoogleMaps();
    return () => {
      if (mapInstanceRef.current) {
        // Track map component unmount
        trackMapEvent('map_component_unmounted', {
          map_type: 'service_areas'
        });
      }
    };
  }, []);

  if (mapError) {
    return (
      <div className="map-fallback">
        <div className="map-fallback-content">
          <h3>Service Areas</h3>
          <p>We service the following areas in Melbourne's West:</p>
          <div className="fallback-areas">
            <div className="fallback-area-group">
              <strong>North:</strong> Bacchus Marsh, Melton, Hillside, Taylors Lakes, Keilor
            </div>
            <div className="fallback-area-group">
              <strong>Central:</strong> Fraser Rise, Taylors Hill, Delahey, St Albans, Sunshine North, Sunshine
            </div>
            <div className="fallback-area-group">
              <strong>South:</strong> Altona North, Altona Meadows, Point Cook, Hoppers Crossing, Werribee
            </div>
            <div className="fallback-area-group">
              <strong>West:</strong> Wyndham Vale, Manor Lakes, Eynesbury, Caroline Springs, Thornhill Park
            </div>
          </div>
          <p className="map-fallback-note">
            <strong>Need a quote?</strong> Call us on <a href="tel:0400000000">0400 000 000</a> or 
            <a href="mailto:lawnsoutwest@outlook.com"> email us</a> for a free quote.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="map-section">
      <div className="service-areas-map-container">
        <div ref={mapRef} className="service-areas-map" />
        <div className="map-legend">
          <div className="legend-item">
            <div className="legend-marker"></div>
            <span>Service Areas</span>
          </div>
          <div className="legend-item">
            <div className="legend-polygon"></div>
            <span>Coverage Zone</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceAreasMap;